/**
 * FieldThresholdHint — compteur de caractères live + état "minimum atteint".
 *
 * Branché à un `<FormTextarea name="..." />` du même `<FormProvider>`.
 * Lit la valeur courante via `useFormContext().watch(name)` ; re-rend à
 * chaque frappe.
 *
 * Usage typique sur la page d'édition d'œuvre :
 *
 *   <FormTextarea name="description_fr" label="Description" rows={6} />
 *   <FieldThresholdHint name="description_fr" minChars={100} />
 *
 * Trois états visibles :
 *  - "Minimum atteint ✓ · 142 caractères" quand current >= min (muted+ok).
 *  - "23 / 100 caractères minimum" quand current < min (muted, pas alarmiste).
 *  - "Description requise pour publier sur sgraphic.art" si vide ET min > 0
 *    (info contextuelle, pas une erreur RHF).
 *
 * Le composant n'est PAS un validator RHF : il informe. Le verrou réel
 * (refus de publish) vit côté backend `publication_service` + côté FE
 * dans `<PortfolioPublishButton>` qui consomme `evaluatePortfolioPublishability`.
 */

import { useFormContext, useWatch, type FieldPath, type FieldValues } from 'react-hook-form'

import './FieldThresholdHint.css'

interface FieldThresholdHintProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  minChars: number
  /** Texte d'introduction (ex: "pour publier sur sgraphic.art"). */
  reason?: string
  /**
   * Mode compact pour rendu sur la ligne du label (slot `labelActions`
   * de `<Field>`). Supprime le margin-top, raccourcit le wording du cas
   * "atteint" en simple compteur.
   */
  inline?: boolean
}

export default function FieldThresholdHint<TFieldValues extends FieldValues = FieldValues>({
  name,
  minChars,
  reason = 'pour publier sur sgraphic.art',
  inline = false,
}: FieldThresholdHintProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>()
  const value = useWatch({ control, name })
  const text = typeof value === 'string' ? value : ''
  const currentChars = text.length
  const isMet = currentChars >= minChars

  const baseClass = inline
    ? 'field-threshold-hint field-threshold-hint--inline'
    : 'field-threshold-hint'

  if (currentChars === 0) {
    return (
      <p className={`${baseClass} field-threshold-hint-muted`}>
        {minChars} caractères minimum {reason}.
      </p>
    )
  }

  if (isMet) {
    return (
      <p className={`${baseClass} field-threshold-hint-ok`}>
        {inline ? `${currentChars} caractères` : `✓ Minimum atteint — ${currentChars} caractères`}
      </p>
    )
  }

  return (
    <p className={`${baseClass} field-threshold-hint-pending`}>
      {inline
        ? `${currentChars} / ${minChars} caractères`
        : `${currentChars} / ${minChars} caractères minimum ${reason}.`}
    </p>
  )
}
